import { colors } from 'ui/theme';
import { Group, Shape, ShapeAttrs } from 'ui/workflows/flowchart/types';
import { DEFAULT_FONT_SIZE, ShapeType } from '../../constants';
import { addLinkedShape, getLinkedShape } from '../helpers';
import { getAssociatedShapeName, GetAssociatedShapeName, getTextWidth } from './textHelpers';

/**
 * Gets the background shape from the text shape
 */
export const getTextBackground = (shape: Shape) => getLinkedShape(shape, ShapeType.Background);

/** Gets the background class name from associated text */
export const getTextBackgroundName = (args: GetAssociatedShapeName) =>
  getAssociatedShapeName({ ...args, suffix: 'background' });

const getBackgroundAttrs = ({ shape, style = {} }: { shape: Shape; style?: ShapeAttrs }) => {
  const bbox = shape.getBBox();
  const { x, width } = bbox;
  const { fontSize = 0, lineHeight = 0 } = { ...shape.attr(), ...style };
  const height = Math.max(fontSize, lineHeight, bbox.height);
  const y = bbox.y - (height - bbox.height) / 2;
  return { x, y, width, height, shapeType: ShapeType.Background, ...style };
};

export const drawTextBackground = ({
  shape,
  className,
  group,
  style = {},
}: GetAssociatedShapeName & { group: Group; style?: ShapeAttrs }) => {
  const name = getTextBackgroundName({ shape, className });
  const attrs = getBackgroundAttrs({ shape, style });
  const bgShape = group.addShape('rect', { name, className: name, attrs });
  addLinkedShape(shape, ShapeType.Background, bgShape);
  // make sure the text is in front of the background
  shape.toFront();
  return bgShape;
};

/**
 * Update the background with new attributes and fit to text
 */
export const updateTextBackground = ({
  shape,
  style = {},
}: {
  shape: Shape;
  style?: ShapeAttrs;
}) => {
  const bgShape = getTextBackground(shape);

  if (bgShape) {
    const currentAttrs = bgShape.attr();
    const updatedAttrs = Object.entries(getBackgroundAttrs({ shape, style })).reduce(
      (acc, [key, value]) => ({ ...acc, [key]: value }),
      currentAttrs,
    );
    bgShape.attr(updatedAttrs);
  }
};

/**
 * Gets the index of a substring in a block of text
 * NOTE: This will only return the first matching index
 */
const getSubstringIndex = (text = '', substring = '') =>
  text.toLowerCase().indexOf(substring.toLowerCase());

interface HighlightTextBase {
  // Substring to highlight
  value: string;
  // The group you wish to add the highlight to -- likely the node group
  group: Group;
  // The style of the background.
  style?: ShapeAttrs;
}

const DEFAULT_HIGHTLIGHT_COLOR = colors.lemon100;

/** Highlights all matching text in an individual line */
const highlightTextInLine = ({
  text = '',
  value = '',
  x,
  y,
  attrs,
  group,
  backgroundShapeName,
  style = { fill: DEFAULT_HIGHTLIGHT_COLOR },
}: HighlightTextBase & {
  // Text in this line
  text: string;
  // Starting x of line
  x: number;
  // Starting y of line
  y: number;
  // The attributes of the text shape
  attrs: ShapeAttrs;
  // The name of the background shape
  backgroundShapeName: string;
}) => {
  const { fontSize = DEFAULT_FONT_SIZE } = attrs;
  const bgs = [];
  let textToProcess = text;
  let startingX = x;
  let i = 0;

  while (getSubstringIndex(textToProcess, value) > -1) {
    const valueStartIndex = getSubstringIndex(textToProcess, value);
    const valueEndIndex = valueStartIndex + value.length;
    const precedingText = textToProcess.substring(0, valueStartIndex);
    // Get the value of the string in the text with capitalization
    const valueText = textToProcess.substring(valueStartIndex, valueEndIndex);
    const backgroundWidth = getTextWidth(valueText, attrs);
    const backgroundX = startingX + getTextWidth(precedingText, attrs);
    const name = `${backgroundShapeName}-result${i}`;
    const bg = group.addShape('rect', {
      name,
      className: name,
      attrs: {
        x: backgroundX,
        y,
        width: backgroundWidth,
        height: fontSize,
        shapeType: ShapeType.Background,
        ...style,
      },
    }) as unknown as Shape;
    bgs.push(bg);
    i += 1;
    // Remove the text that was just process from the text string
    textToProcess = textToProcess.substring(valueEndIndex, textToProcess.length);
    // Move the starting position to reflect the initial position in the new string
    startingX = backgroundX + backgroundWidth;
  }

  return bgs;
};

/** Highlights all matching text within a text shape */
export const highlightMatchingText = ({
  shape,
  value = '',
  group,
  style = { fill: DEFAULT_HIGHTLIGHT_COLOR },
  namePrefix,
}: HighlightTextBase & {
  // The shape with the text
  shape: Shape;
  // The prefix of the background shape
  namePrefix?: string;
}) => {
  const attrs = shape.get('attrs');
  const { x, y } = shape.getBBox();
  const { text = '', fontSize = DEFAULT_FONT_SIZE, lineHeight } = attrs;
  const rowHeight = lineHeight ?? fontSize;
  const backgroundNamePrefix = namePrefix ?? getTextBackgroundName({ shape });
  const rows: string[] = text.split('\n');
  let startingY = y;

  return rows.reduce((acc: Shape[], row, i) => {
    const rowBackgrounds = highlightTextInLine({
      attrs,
      text: row,
      value,
      x,
      y: startingY,
      group,
      backgroundShapeName: `${backgroundNamePrefix}-row${i}`,
      style,
    });
    startingY += rowHeight;
    return [...acc, ...rowBackgrounds];
  }, []);
};
